/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 */

import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t, LocaleController } from '../services/i18n.js';

/** Payload for `participant-saved` — matches form-side participant stubs. */
export type TdAddParticipantSavedDetail = {
  name: string;
  email: string;
  confirmed: boolean;
};

/** Optional pre-fill passed into {@link TdAddParticipant.show}. */
export type TdAddParticipantPrefill = {
  name?: string;
  email?: string;
};

/**
 * Dialog to add a disclosure participant (name + email).
 *
 * Open with {@link TdAddParticipant.show} (optionally pre-filling fields).
 * Parents listen for `participant-saved` (bubbles, composed) or
 * `participant-add-cancelled` (bubbles, composed).
 *
 * The dialog renders into light DOM and uses per-instance ids
 * (`td-add-participant-name-{n}` / `td-add-participant-email-{n}`) so multiple
 * instances on the same page don't collide in the global id namespace.
 */
@customElement('td-add-participant')
export class TdAddParticipant extends LitElement {
  @property({ type: Boolean }) open = false;
  @state() private _prefillName = '';
  @state() private _prefillEmail = '';

  /** Re-renders this component when the UI locale changes (#89). */
  readonly localeController = new LocaleController(this);

  // Per-instance id used to namespace input element ids in the global DOM.
  private static _instanceCounter = 0;
  private readonly _instanceId: number = ++TdAddParticipant._instanceCounter;

  override createRenderRoot(): HTMLElement {
    return this;
  }

  /**
   * Open the dialog, optionally pre-filling the name and email fields.
   * Safe to call repeatedly — re-opens with fresh pre-fill values.
   */
  show(participant: TdAddParticipantPrefill = {}): void {
    this._prefillName = participant.name ?? '';
    this._prefillEmail = participant.email ?? '';
    this.open = true;
  }

  private get _nameInputId(): string {
    return `td-add-participant-name-${this._instanceId}`;
  }

  private get _emailInputId(): string {
    return `td-add-participant-email-${this._instanceId}`;
  }

  private _inputValue(id: string): string {
    const el = this.querySelector(`#${id}`);
    if (!el || !('value' in el)) return '';
    const raw = (el as { value: unknown }).value;
    return typeof raw === 'string' ? raw.trim() : '';
  }

  private _handleFormSubmit = (e: Event): void => {
    e.preventDefault();
    this._save();
  };

  private _save = (): void => {
    const name = this._inputValue(this._nameInputId);
    const email = this._inputValue(this._emailInputId);
    if (!name || !email) return;

    const detail: TdAddParticipantSavedDetail = {
      name,
      email,
      confirmed: false,
    };
    this.dispatchEvent(
      new CustomEvent<TdAddParticipantSavedDetail>('participant-saved', {
        bubbles: true,
        composed: true,
        detail,
      })
    );
    this.open = false;
  };

  private _handleCancel = (): void => {
    this.dispatchEvent(
      new CustomEvent('participant-add-cancelled', {
        bubbles: true,
        composed: true,
      })
    );
    this.open = false;
  };

  override render(): TemplateResult {
    return html`
      <wa-dialog label=${t('addparticipant.title')} ?open=${this.open}>
        <div class="wa-stack">
          <form @submit=${this._handleFormSubmit} class="wa-stack">
            <wa-input
              id=${this._nameInputId}
              name="name"
              type="text"
              label=${t('addparticipant.nameLabel')}
              placeholder=${t('addparticipant.namePlaceholder')}
              .value=${this._prefillName}
              required
            ></wa-input>
            <wa-input
              id=${this._emailInputId}
              name="email"
              type="email"
              label=${t('addparticipant.emailLabel')}
              placeholder=${t('addparticipant.emailPlaceholder')}
              .value=${this._prefillEmail}
              required
            ></wa-input>
          </form>
          <div class="wa-cluster">
            <wa-button type="button" variant="brand" @click=${this._save}>
              ${t('addparticipant.save')}
            </wa-button>
            <wa-button
              type="button"
              variant="neutral"
              @click=${this._handleCancel}
            >
              ${t('common.cancel')}
            </wa-button>
          </div>
        </div>
      </wa-dialog>
    `;
  }
}
